import { Icon, IconProps, Stack, Table, TableColumns, Tooltip } from '@sixfold/common-ui';
import { arrayToMultiMap, exhaustiveCheck, getWithDefault } from '@sixfold/typed-primitives';
import React from 'react';

import { FormattedDate } from '../../../components/date_formatting/formatted_date';
import { CompanyOnboardingQuery, CompanySignupVisibilitySourceStatus } from '../../../lib/graphql';
import { beautifyString } from '../../../lib/util/string';
import {
  getVisibilitySourceDescription,
  getVisibilitySourceLabel,
  getVisibilitySourceStatusLabel,
} from '../../../relationships/visibility_sources';

type VisibilitySourceIconConfig = {
  name: NonNullable<IconProps['name']>;
  color: string;
};

export function getVisibilitySourceIcon(status: CompanySignupVisibilitySourceStatus): VisibilitySourceIconConfig {
  switch (status) {
    case CompanySignupVisibilitySourceStatus.IDENTIFIED:
      return { name: 'small-circular-clock', color: 'var(--color-neutral-text-subtle, #767676)' };
    case CompanySignupVisibilitySourceStatus.ACTIVE:
      return { name: 'small-check', color: 'var(--color-success-text-accent, #21ba45)' };
    case CompanySignupVisibilitySourceStatus.DEACTIVATED:
      return { name: 'small-cross', color: 'var(--color-danger-text-accent, #b33a3a)' };
    default:
      return exhaustiveCheck(status);
  }
}

type EventRow = {
  id: string;
  time: React.ReactNode;
  event: string;
};

const sourceEventColumns: TableColumns<EventRow> = [
  { id: 'time', title: 'Time', cellContent: ({ value }) => <>{value}</> },
  { id: 'event', title: 'Event' },
];

export const SignupVisibilitySources: React.FC<{
  visibilitySources: NonNullable<NonNullable<CompanyOnboardingQuery['company']>['signupVisibilitySources']>;
  events: NonNullable<CompanyOnboardingQuery['company']>['unifiedSignupEvents'];
}> = ({ visibilitySources, events }) => {
  if (visibilitySources.length === 0) {
    return <>-</>;
  }

  const eventsBySource = arrayToMultiMap(events ?? [], ({ visibilitySource }) => visibilitySource ?? '-');

  return (
    <Stack direction="row" gap="small">
      {visibilitySources.map((source, idx) => {
        const sourceEvents = getWithDefault(eventsBySource, source.type, []);
        const icon = getVisibilitySourceIcon(source.status);

        const description = getVisibilitySourceDescription(source.type);

        const tooltipContent = (
          <div style={{ minWidth: '300px' }}>
            <p style={{ margin: '0 0 4px' }}>
              <span style={{ fontWeight: 600 }}>Visibility source:</span> {getVisibilitySourceLabel(source.type)}
            </p>
            {description !== null && <p style={{ margin: '0 0 4px' }}>{description}</p>}
            <p style={{ margin: '0 0 4px' }}>
              <span style={{ fontWeight: 600 }}>Status:</span> {getVisibilitySourceStatusLabel(source.status)}
            </p>
            {sourceEvents.length > 0 && (
              <Table
                data={sourceEvents.map(({ eventTime, type }, eventIdx) => ({
                  id: String(eventIdx),
                  time: <FormattedDate date={eventTime} />,
                  event: beautifyString(type),
                }))}
                columns={sourceEventColumns}
              />
            )}
          </div>
        );

        return (
          <Tooltip key={idx} content={tooltipContent}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--size-spacing-xsmall, 4px)' }}>
              <Icon name={icon.name} color={icon.color} />
              {getVisibilitySourceLabel(source.type)}
            </span>
          </Tooltip>
        );
      })}
    </Stack>
  );
};
